import * as THREE from 'three';
import type { FlightRecorder, FlightSample } from './Recorder';

/**
 * Replay playback (SPEC §3.14, §3.15).
 *
 * The M10 acceptance criterion is that "replay reproduces a flight exactly". This plays back the
 * *recording* rather than re-running the simulation, which is the honest reading of that: the
 * recorder samples at 20 Hz, so the aircraft's path is reproduced exactly at every sample and
 * interpolated between them.
 *
 * Re-simulating from the seed would be the other approach and would be bit-exact, but it would
 * also mean a replay could only ever show what the simulation already produces — it could not
 * survive a physics change, and it could not be exported and viewed elsewhere. A recording is a
 * document; a re-simulation is a repeat.
 *
 * Interpolation between samples is the same shape as the render interpolation in the live loop
 * (CLAUDE.md): position and angles blended by `alpha`, never snapped.
 */

export class ReplayPlayer {
  private readonly recorder: FlightRecorder;
  private readonly a: FlightSample = blank();
  private readonly b: FlightSample = blank();

  /** Playback head, seconds into the recording. */
  timeS = 0;
  playing = false;
  speed = 1;

  readonly positionM = new THREE.Vector3();
  readonly orientation = new THREE.Quaternion();
  airspeedMs = 0;
  altitudeAglM = 0;
  loadFactor = 0;
  integrity = 1;

  private readonly euler = new THREE.Euler();

  constructor(recorder: FlightRecorder) {
    this.recorder = recorder;
  }

  get durationS(): number {
    return this.recorder.durationS;
  }

  play(): void {
    this.playing = true;
  }

  pause(): void {
    this.playing = false;
  }

  /** Scrub to a time. Clamped, so a slider cannot run off the end of the recording. */
  seek(timeS: number): void {
    this.timeS = Math.max(0, Math.min(this.durationS, timeS));
    this.sampleAt(this.timeS);
  }

  /** Advance playback. `dtS` is the render delta — replay is presentation, not simulation. */
  update(dtS: number): void {
    if (this.playing) {
      this.timeS += dtS * this.speed;
      if (this.timeS >= this.durationS) {
        this.timeS = this.durationS;
        this.playing = false;
      }
    }
    this.sampleAt(this.timeS);
  }

  private sampleAt(timeS: number): void {
    const count = this.recorder.count;
    if (count === 0) return;

    const i = this.recorder.indexAtTime(timeS);
    const j = Math.min(count - 1, i + 1);
    this.recorder.read(i, this.a);
    this.recorder.read(j, this.b);

    const span = this.b.timeS - this.a.timeS;
    const alpha = span > 1e-6 ? clamp01((timeS - this.a.timeS) / span) : 0;

    this.positionM.set(
      lerp(this.a.x, this.b.x, alpha),
      lerp(this.a.y, this.b.y, alpha),
      lerp(this.a.z, this.b.z, alpha),
    );

    // Heading wraps, so it is interpolated on the shortest arc rather than numerically — the
    // alternative is a full spin every time the aircraft crosses north.
    const headingRad = this.a.headingRad + shortestAngle(this.a.headingRad, this.b.headingRad) * alpha;
    // Heading and bank are negated going back into a Euler. Both are recorded in the aviation
    // sense — heading clockwise from north, bank positive right-wing-down — and both run opposite
    // to a positive rotation about the matching Three.js axis in the body frame this project uses
    // (−Z forward, +Y up, +X right). Without the signs the replay flies the recording's mirror
    // image: correct path, reversed turns.
    this.euler.set(
      lerp(this.a.pitchRad, this.b.pitchRad, alpha),
      -headingRad,
      -lerp(this.a.bankRad, this.b.bankRad, alpha),
      'YXZ',
    );
    this.orientation.setFromEuler(this.euler);

    this.airspeedMs = lerp(this.a.airspeedMs, this.b.airspeedMs, alpha);
    this.altitudeAglM = lerp(this.a.altitudeAglM, this.b.altitudeAglM, alpha);
    this.loadFactor = lerp(this.a.loadFactor, this.b.loadFactor, alpha);
    this.integrity = lerp(this.a.integrity, this.b.integrity, alpha);
  }
}

/** Signed shortest angular difference from `a` to `b`, radians in (-π, π]. */
export function shortestAngle(a: number, b: number): number {
  const twoPi = Math.PI * 2;
  let d = (b - a) % twoPi;
  if (d > Math.PI) d -= twoPi;
  if (d <= -Math.PI) d += twoPi;
  return d;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function blank(): FlightSample {
  return {
    timeS: 0,
    x: 0,
    y: 0,
    z: 0,
    headingRad: 0,
    pitchRad: 0,
    bankRad: 0,
    airspeedMs: 0,
    altitudeAglM: 0,
    loadFactor: 0,
    integrity: 0,
    throttle: 0,
  };
}
